import PropTypes from "prop-types";

/**
 * RealisticSplitAC – High-fidelity 3D split air conditioner indoor unit.
 * Features:
 * - Bright, clearly visible wind streams blowing directly out of the lower discharge grille
 * - Airflow velocity scales dynamically with live value (CFM / temperature load)
 * - Pristine casing with zero static overlays or misplaced indicators
 */
const RealisticSplitAC = ({
  liveValue,
  isRunning,
  isOffline,
  statusColor = "grey",
  speedScale = 1,
  ratio = 1,
  opacity = 1,
  width = "100%",
  height = "100%",
}) => {
  const isCooling = isRunning && !isOffline;
  const isOff = !isOffline && !isRunning;
  const dotColor = statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";

  const val = typeof liveValue === "number" ? liveValue : (isCooling ? 24 : 0);
  const effectiveSpeed = Math.max(0.3, speedScale || 1);
  const windDuration = isCooling ? `${Math.max(0.3, 1.2 / effectiveSpeed)}s` : "0s";

  return (
    <div
      className={`scada-realistic-container ${isOffline ? "scada-realistic--offline" : ""}`}
      style={{
        width,
        height,
        opacity,
      }}
    >
      {/* 3D Realistic Base Image */}
      <img
        src="/scada-realistic/split-ac.png"
        alt="SCADA Realistic Split AC"
        className="scada-realistic-img"
      />

      {/* Dynamic Animated Overlay (SVG synchronized in 498x178 space, overflow visible for air wind) */}
      <svg
        className="scada-realistic-overlay"
        viewBox="0 0 498 178"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ overflow: "visible" }}
      >
        <defs>
          <filter id="acWindGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>

          <linearGradient id="splitWindGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#bae6fd" stopOpacity="0.95" />
            <stop offset="60%" stopColor="#38bdf8" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#0284c7" stopOpacity="0" />
          </linearGradient>

          <filter id="acLedGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* ── Front Right Cover Digital Display & Pilot LED (3 Distinct States) ── */}
        {/* State 1 (Cooling): Cyan Temp + Green LED. State 2 (Off): Red '--' + Red LED. State 3 (Offline): Blank + Grey LED */}
        <g>
          {/* Digital LED Temperature Readout (Active when cooling) */}
          {isCooling && (
            <text
              x="395"
              y="74"
              fill="#38bdf8"
              fontSize="15"
              fontWeight="bold"
              fontFamily="monospace"
              textAnchor="middle"
              filter="url(#acLedGlow)"
              style={{ letterSpacing: "1px" }}
            >
              {`${Math.round(val)}°`}
            </text>
          )}

          {/* Front Panel Power / Status Indicator Pilot LED */}
          <circle
            cx="445"
            cy="69"
            r="4.5"
            fill={dotColor}
            stroke="#ffffff"
            strokeWidth="1.2"
            filter={isCooling || isOff ? "url(#acLedGlow)" : "none"}
            style={{
              animation: isCooling
                ? "scada-led-blink 2s infinite ease-in-out"
                : isOff
                ? "none"
                : "none",
            }}
          />
          {isCooling && <circle cx="443.5" cy="67.5" r="1.5" fill="#ffffff" opacity="0.85" />}
        </g>

        {/* ── Bright Wind Streams Blowing Out of the Bottom Grille ── */}
        {isRunning && !isOffline && (
          <g filter="url(#acWindGlow)">
            {/* Stream 1 (Far Left) */}
            <path
              d="M 110 160 Q 95 185 75 215"
              stroke="url(#splitWindGrad)"
              strokeWidth="3.2"
              strokeLinecap="round"
              fill="none"
              style={{ animation: `scada-breeze-drift ${windDuration} infinite ease-out` }}
            />

            {/* Stream 2 (Mid-Left) */}
            <path
              d="M 155 162 Q 142 188 125 218"
              stroke="url(#splitWindGrad)"
              strokeWidth="3.2"
              strokeLinecap="round"
              fill="none"
              style={{ animation: `scada-breeze-drift ${windDuration} infinite 0.15s ease-out` }}
            />

            {/* Stream 3 (Center-Left) */}
            <path
              d="M 205 164 Q 198 190 188 220"
              stroke="url(#splitWindGrad)"
              strokeWidth="3.2"
              strokeLinecap="round"
              fill="none"
              style={{ animation: `scada-breeze-drift ${windDuration} infinite 0.3s ease-out` }}
            />

            {/* Stream 4 (Center) */}
            <path
              d="M 249 164 Q 249 192 249 222"
              stroke="url(#splitWindGrad)"
              strokeWidth="3.5"
              strokeLinecap="round"
              fill="none"
              style={{ animation: `scada-breeze-drift ${windDuration} infinite 0.45s ease-out` }}
            />

            {/* Stream 5 (Center-Right) */}
            <path
              d="M 293 164 Q 300 190 310 220"
              stroke="url(#splitWindGrad)"
              strokeWidth="3.2"
              strokeLinecap="round"
              fill="none"
              style={{ animation: `scada-breeze-drift ${windDuration} infinite 0.6s ease-out` }}
            />

            {/* Stream 6 (Mid-Right) */}
            <path
              d="M 343 162 Q 356 188 373 218"
              stroke="url(#splitWindGrad)"
              strokeWidth="3.2"
              strokeLinecap="round"
              fill="none"
              style={{ animation: `scada-breeze-drift ${windDuration} infinite 0.75s ease-out` }}
            />

            {/* Stream 7 (Far Right) */}
            <path
              d="M 388 160 Q 403 185 423 215"
              stroke="url(#splitWindGrad)"
              strokeWidth="3.2"
              strokeLinecap="round"
              fill="none"
              style={{ animation: `scada-breeze-drift ${windDuration} infinite 0.9s ease-out` }}
            />
          </g>
        )}
      </svg>
    </div>
  );
};

RealisticSplitAC.propTypes = {
  liveValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unit: PropTypes.string,
  isRunning: PropTypes.bool,
  isOffline: PropTypes.bool,
  statusColor: PropTypes.string,
  speedScale: PropTypes.number,
  opacity: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default RealisticSplitAC;
